import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, toast } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sparkles, Send, Check, X, TrendingUp, TrendingDown, Info, Search, ChevronDown, Bot, MessageCircle, AlertTriangle, Shield, ChevronRight, Loader2, Minus, Plus, Building2, Users, UserCircle, BarChart3, Activity, Clock, Target, AlertCircle, Brain, Heart, ExternalLink, Zap } from "lucide-react";
import { AdvisoryCard } from "@/components/AdvisoryCard";
import SEOHead, { PageSEO } from "@/components/SEOHead";
import useRealtimeData, { useRealtimeFnO } from "@/hooks/useRealtimeData";
import { useOptionChainStream } from "@/hooks/useOptionChainStream";
import { useRestOnce } from "@/hooks/useRestOnce";
import { isMarketOpen } from "@/utils/marketHours";
import { useWatchlistCheck } from "@/hooks/useWatchlist";
import { ConnectionBanner, LiveDot } from "@/components/StaleIndicator";
import { Star, Bell } from "lucide-react";
import { getOptionChartUrl } from "@/utils/tradingViewMapping";

// ── Feature Flag: Pro UI (default ON). Set REACT_APP_OPTION_CHAIN_PRO_UI=false to revert. ──
const USE_PRO_UI = process.env.REACT_APP_OPTION_CHAIN_PRO_UI !== 'false';
const OptionChainClassic = lazy(() => import('./OptionChainClassic'));

// Available instruments for Option Chain — fetched from canonical backend at /api/fo/instruments
// No hardcoded lists. Seed fallback used only during initial load.
const SEED_INSTRUMENTS = [
  { symbol: 'NIFTY', name: 'NIFTY 50', category: 'Index', lotSize: 50, exchange: 'NSE', derivExchange: 'NFO' },
  { symbol: 'BANKNIFTY', name: 'BANK NIFTY', category: 'Index', lotSize: 25, exchange: 'NSE', derivExchange: 'NFO' },
  { symbol: 'FINNIFTY', name: 'FIN NIFTY', category: 'Index', lotSize: 40, exchange: 'NSE', derivExchange: 'NFO' },
  { symbol: 'MIDCPNIFTY', name: 'MIDCAP NIFTY', category: 'Index', lotSize: 50, exchange: 'NSE', derivExchange: 'NFO' },
  { symbol: 'SENSEX', name: 'SENSEX', category: 'Index', lotSize: 10, exchange: 'BSE', derivExchange: 'BFO' },
  { symbol: 'BANKEX', name: 'BANKEX', category: 'Index', lotSize: 15, exchange: 'BSE', derivExchange: 'BFO' },
];

// Generate expiry dates dynamically based on underlying
// Each index has a different expiry day:
// NIFTY/MIDCPNIFTY: Thursday, BANKNIFTY: Wednesday, FINNIFTY: Tuesday, SENSEX: Friday
const generateExpiryDates = (underlying = "NIFTY") => {
  const today = new Date();
  const expiries = [];
  
  // Expiry day mapping (0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat)
  const expiryDayMap = {
    'NIFTY': 4,      // Thursday
    'NIFTY50': 4,    // Thursday
    'BANKNIFTY': 3,  // Wednesday
    'FINNIFTY': 2,   // Tuesday
    'MIDCPNIFTY': 1, // Monday
    'SENSEX': 5,     // Friday
    'BANKEX': 1,     // Monday
  };
  
  const expiryDay = expiryDayMap[underlying.toUpperCase()] || 4; // Default Thursday
  
  let currentDate = new Date(today);
  
  while (expiries.length < 10) {
    // Calculate days until next expiry
    let daysUntilExpiry = (expiryDay - currentDate.getDay() + 7) % 7;
    
    // If today is expiry day and we already have some expiries, skip to next week
    if (daysUntilExpiry === 0 && expiries.length > 0) {
      daysUntilExpiry = 7;
    }
    // If today is expiry day and market is closed (after 4 PM), skip to next week
    else if (daysUntilExpiry === 0 && today.getHours() >= 16) {
      daysUntilExpiry = 7;
    }
    
    const expDate = new Date(currentDate);
    expDate.setDate(currentDate.getDate() + daysUntilExpiry);
    
    // Check if this is a monthly expiry (last occurrence of expiry day in the month)
    const nextWeek = new Date(expDate);
    nextWeek.setDate(expDate.getDate() + 7);
    const isMonthly = nextWeek.getMonth() !== expDate.getMonth();
    
    // Calculate days to expiry from today
    const daysToExpiry = Math.max(0, Math.ceil((expDate - today) / (1000 * 60 * 60 * 24)));
    
    // Format the date
    const day = expDate.getDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[expDate.getMonth()];
    const year = expDate.getFullYear().toString().slice(-2);
    
    expiries.push({
      date: expDate,
      label: `${day} ${month}`,
      fullLabel: `${day} ${month} ${year}`,
      value: expDate.toISOString().split('T')[0].replace(/-/g, ''),
      isWeekly: !isMonthly,
      isMonthly: isMonthly,
      daysToExpiry: daysToExpiry
    });
    
    // Move to the day after this expiry to find the next one
    currentDate = new Date(expDate);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return expiries;
};


// FUTURES_INSTRUMENTS removed — canonical source is /api/fo/instruments

function OptionChainPro() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlSymbol = searchParams.get('symbol') || 'NIFTY';
  const urlExchange = searchParams.get('exchange') || '';

  const [selectedSymbol, setSelectedSymbol] = useState(urlSymbol);
  const [optionChain, setOptionChain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [strategyText, setStrategyText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiStrategy, setAiStrategy] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showInstrumentDropdown, setShowInstrumentDropdown] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [heatmapGreek, setHeatmapGreek] = useState("delta");
  const [showGreeks, setShowGreeks] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [basketMode, setBasketMode] = useState(false);
  const [basketItems, setBasketItems] = useState([]);
  const [buildingStrategy, setBuildingStrategy] = useState(false);
  const [sameQtyEnabled, setSameQtyEnabled] = useState(false);
  // StoxCap Pro: Mobile view switcher (calls | full | puts)
  const [mobileView, setMobileView] = useState('full');
  // Max Coverage Mode: subscribe visible + near-ATM strikes individually via WS
  const [maxCoverage, setMaxCoverage] = useState(false);
  const MAX_COVERAGE_RANGE = 20; // strikes above + below ATM

  // F&O instruments from canonical backend API
  const [fnoInstruments, setFnoInstruments] = useState(SEED_INSTRUMENTS);
  useEffect(() => {
    axios.get(`${API}/fo/instruments`).then(res => {
      const items = (res.data?.instruments || []).map(i => ({
        symbol: i.symbol, name: i.name, category: i.category,
        lotSize: i.lot_size, sector: i.sector,
        exchange: i.exchange, derivExchange: i.deriv_exchange || 'NFO',
      }));
      if (items.length > 0) setFnoInstruments(items);
    }).catch(() => {});
  }, []);
  
  // Real-time data for spot price
  const spotSymbolMap = {
    'NIFTY': 'NIFTY 50',
    'BANKNIFTY': 'NIFTY BANK',
    'FINNIFTY': 'NIFTY FIN SERVICE',
    'MIDCPNIFTY': 'NIFTY MID SELECT',
    'SENSEX': 'SENSEX',
    'BANKEX': 'BANKEX',
  };
  const spotSymbol = spotSymbolMap[selectedSymbol] || selectedSymbol;
  // Derive spot exchange from canonical instrument list: BSE for BFO (SENSEX/BANKEX), NSE otherwise
  const spotExchange = useMemo(() => {
    const inst = fnoInstruments.find(i => i.symbol === selectedSymbol);
    if (inst?.exchange) return inst.exchange;
    if (inst?.derivExchange === 'BFO') return 'BSE';
    return 'NSE';
  }, [fnoInstruments, selectedSymbol]);
  const { ticks: spotTicks, isConnected: isLiveConnected, marketOpen: ocMarketOpen } = useRealtimeData([spotSymbol], spotExchange);
  const liveSpotPrice = spotTicks[spotSymbol]?.last_price;
  const liveSpotChange = spotTicks[spotSymbol]?.change_percent;
  const [spotFlash, setSpotFlash] = useState(false);
  const prevSpotRef = useRef(null);
  const ocScrollRef = useRef(null);
  const atmRowRef = useRef(null);
  const hasScrolledToATM = useRef(false);
  
  // Flash effect when spot price changes
  useEffect(() => {
    if (liveSpotPrice && liveSpotPrice !== prevSpotRef.current) {
      setSpotFlash(true);
      setTimeout(() => setSpotFlash(false), 300);
      prevSpotRef.current = liveSpotPrice;
    }
  }, [liveSpotPrice]);
  
  // Expiry Date State - Fetched from NSE API via shared hook
  const [expiryDates, setExpiryDates] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState(0); // Index of selected expiry
  const [showExpiryDropdown, setShowExpiryDropdown] = useState(false);
  const [expiryDataSource, setExpiryDataSource] = useState(null);
  
  // Fetch expiry dates via useRestOnce (no polling)
  const { data: expiryRaw, loading: expiryLoading, error: expiryError, refresh: refreshExpiries } = useRestOnce(
    `/market/optionchain/expiries/${selectedSymbol}`,
    { cacheKey: `oc-expiries-${selectedSymbol}` }
  );

  // Transform expiry data when it arrives
  useEffect(() => {
    if (expiryRaw?.expiries?.length > 0) {
      const formattedExpiries = expiryRaw.expiries.map((exp) => ({
        date: new Date(exp.iso),
        label: exp.label,
        fullLabel: exp.full_date,
        value: exp.iso.replace(/-/g, ''),
        nseFormat: exp.nse_format || exp.full_date,
        isWeekly: exp.is_weekly,
        isMonthly: exp.is_monthly,
        daysToExpiry: exp.days_to_expiry
      }));
      setExpiryDates(formattedExpiries);
      setExpiryDataSource(expiryRaw.data_source);
      setSelectedExpiry(0);
    } else if (expiryRaw !== null && expiryRaw?.expiries?.length === 0) {
      // Explicit empty array returned — fallback to local generation
      setExpiryDates(generateExpiryDates(selectedSymbol));
      setExpiryDataSource("LOCAL_FALLBACK");
    }
  }, [expiryRaw, selectedSymbol]);

  // Fallback: if expiry fetch finished (loading=false) but no data arrived, generate locally
  useEffect(() => {
    if (!expiryLoading && expiryDates.length === 0) {
      setExpiryDates(generateExpiryDates(selectedSymbol));
      setExpiryDataSource("LOCAL_FALLBACK");
    }
  }, [expiryLoading, expiryDates.length, selectedSymbol]);
  
  // Order Panel States
  const [orderPanelOpen, setOrderPanelOpen] = useState(false);
  const [orderType, setOrderType] = useState("delivery"); // delivery or intraday
  const [orderAction, setOrderAction] = useState("BUY");
  const [orderQuantity, setOrderQuantity] = useState(1);
  const [orderPrice, setOrderPrice] = useState(0);
  const [selectedStrike, setSelectedStrike] = useState(null);
  const [selectedOptionType, setSelectedOptionType] = useState("CE");
  const [userBalance, setUserBalance] = useState(1000000);
  
  // Strategy Lot Quantity State
  const [strategyLots, setStrategyLots] = useState(1);

  // Alert creation state
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [alertType, setAlertType] = useState("above");
  const [alertPrice, setAlertPrice] = useState("");
  const [alertNote, setAlertNote] = useState("");
  const [alertLoading, setAlertLoading] = useState(false);

  // Watchlist
  const { inWatchlist, toggle: toggleWatchlist, loading: wlLoading } = useWatchlistCheck(selectedSymbol);

  // Live price update for order panel: keep orderPrice in sync with WS ticks
  useEffect(() => {
    if (!orderPanelOpen || !selectedStrike || !optionChain?.strikes) return;
    const liveStrike = optionChain.strikes.find(s => s.strike === selectedStrike.strike);
    if (!liveStrike) return;
    const side = selectedOptionType === 'CE' ? liveStrike.ce : liveStrike.pe;
    if (side?.ltp && side.ltp > 0) {
      setOrderPrice(side.ltp);
    }
  }, [orderPanelOpen, selectedStrike, selectedOptionType, optionChain]);

  const handleCreateAlert = async () => {
    const token = localStorage.getItem("token");
    if (!token) { toast.error("Please login first"); return; }
    if (!alertPrice) { toast.error("Enter a target price"); return; }
    setAlertLoading(true);
    try {
      const res = await fetch(`${API}/alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          symbol: selectedSymbol,
          alert_type: alertType,
          target_price: parseFloat(alertPrice),
          note: alertNote || `${selectedSymbol} ${alertType} ${alertPrice}`,
          exchange: currentInstrument?.exchange || "NSE",
          instrument_type: currentInstrument?.category === "Index" ? "index" : "equity",
        })
      });
      if (res.ok) {
        toast.success(`Alert created: ${selectedSymbol} ${alertType} ₹${alertPrice}`);
        setShowAlertDialog(false);
        setAlertPrice("");
        setAlertNote("");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to create alert");
      }
    } catch (e) {
      toast.error("Failed to create alert");
    } finally {
      setAlertLoading(false);
    }
  };

  const handleWatchlistToggle = async () => {
    const result = await toggleWatchlist({
      name: currentInstrument?.name || selectedSymbol,
      exchange: currentInstrument?.exchange || "NSE",
      segment: currentInstrument?.category === "Index" ? "index" : "equity",
      asset_class: "fo",
    });
    if (result) {
      toast.success(result.in_watchlist ? `${selectedSymbol} added to watchlist` : `${selectedSymbol} removed from watchlist`);
    }
  };
  
  // ── C3: WebSocket Option Chain Stream ──────────────────────
  // Derive the expiry in YYYY-MM-DD format for the WS room
  const wsExpiry = useMemo(() => {
    const ed = expiryDates[selectedExpiry];
    if (!ed) return null;
    // Use ISO date from the expiry data
    if (ed.date instanceof Date && !isNaN(ed.date)) {
      return ed.date.toISOString().split('T')[0];
    }
    // Fallback: parse from value (YYYYMMDD)
    if (ed.value && ed.value.length === 8) {
      return `${ed.value.slice(0,4)}-${ed.value.slice(4,6)}-${ed.value.slice(6,8)}`;
    }
    return null;
  }, [expiryDates, selectedExpiry]);

  const {
    update: wsUpdate,
    freshnessStatus: wsFreshness,
    integrity: wsIntegrity,
    isStreaming: wsIsStreaming,
    isPollingFallback: wsIsPolling,
  } = useOptionChainStream({
    underlying: selectedSymbol,
    expiry: wsExpiry,
  });

  // Merge WS updates into option chain state — ADDITIVE merge, never erase REST fields
  useEffect(() => {
    if (!wsUpdate || !wsUpdate.rows || wsUpdate.rows.length === 0) return;
    setOptionChain(prev => {
      const spot = wsUpdate.spot ?? prev?.spot_price;
      // Build a map from existing strikes for merge
      const existingMap = {};
      (prev?.strikes || []).forEach(s => { existingMap[s.strike] = s; });

      // Merge WS rows into existing data — only overwrite non-null WS values
      const strikes = wsUpdate.rows.map(row => {
        const existing = existingMap[row.strike] || {};
        const mergeSide = (wsData, existData) => {
          if (!wsData && !existData) return {};
          if (!wsData) return existData || {};
          if (!existData) return wsData;
          // Merge: WS non-null values win, REST values kept for anything WS doesn't provide
          // Safety: don't overwrite positive price/oi/volume with 0
          const SAFE_KEYS = new Set(['ltp', 'oi', 'volume', 'prev_close']);
          const merged = { ...existData };
          for (const key of Object.keys(wsData)) {
            if (wsData[key] !== null && wsData[key] !== undefined) {
              if (SAFE_KEYS.has(key) && wsData[key] === 0 && existData[key] > 0) continue;
              merged[key] = wsData[key];
            }
          }
          return merged;
        };
        return {
          strike: row.strike,
          ce: mergeSide(row.ce, existing.ce),
          pe: mergeSide(row.pe, existing.pe),
        };
      });
      return {
        ...prev,
        spot_price: spot,
        strikes,
        freshness_status: wsUpdate.freshness_status,
        source: wsUpdate.source_mode,
        as_of: wsUpdate.as_of,
        integrity: wsUpdate.integrity,
        data_source: wsUpdate.source_mode,
      };
    });
  }, [wsUpdate]);
  // ── End C3 ─────────────────────────────────────────────────

  // ── Max Coverage: Subscribe to individual strike trading symbols via WS ──
  const strikeTradingSymbols = useMemo(() => {
    if (!maxCoverage || !optionChain?.strikes) return [];
    const strikes = optionChain.strikes;
    const spotPrice = liveSpotPrice || optionChain?.spot_price || 0;
    
    // Find ATM index
    let atmIdx = 0;
    let minDiff = Infinity;
    strikes.forEach((s, i) => {
      const diff = Math.abs(s.strike - spotPrice);
      if (diff < minDiff) { minDiff = diff; atmIdx = i; }
    });
    
    // Subscribe to strikes in range: ATM +/- MAX_COVERAGE_RANGE
    const start = Math.max(0, atmIdx - MAX_COVERAGE_RANGE);
    const end = Math.min(strikes.length, atmIdx + MAX_COVERAGE_RANGE + 1);
    const symbols = [];
    for (let i = start; i < end; i++) {
      const s = strikes[i];
      if (s.ce?.tradingsymbol) symbols.push(s.ce.tradingsymbol);
      if (s.pe?.tradingsymbol) symbols.push(s.pe.tradingsymbol);
    }
    return symbols;
  }, [maxCoverage, optionChain?.strikes, liveSpotPrice, optionChain?.spot_price, MAX_COVERAGE_RANGE]);

  // Get current instrument info — with fallback for CDS/MCX symbols from URL
  // NOTE: Must be computed before derivExchange (used by useRealtimeFnO below)
  const _CDS_SYMBOLS = { USDINR: { name: "USD/INR", lotSize: 1000 }, EURINR: { name: "EUR/INR", lotSize: 1000 }, GBPINR: { name: "GBP/INR", lotSize: 1000 }, JPYINR: { name: "JPY/INR", lotSize: 1000 } };
  const _MCX_LOT = { GOLD: 100, GOLDM: 10, SILVER: 30, SILVERM: 5, CRUDEOIL: 100, CRUDEOILM: 10, NATURALGAS: 1250, NATGASMINI: 250, COPPER: 2500, ZINC: 5000, ALUMINIUM: 5000, LEAD: 5000, NICKEL: 1500 };
  const currentInstrument = fnoInstruments.find(i => i.symbol === selectedSymbol)
    || (_CDS_SYMBOLS[selectedSymbol.toUpperCase()] ? { symbol: selectedSymbol.toUpperCase(), name: _CDS_SYMBOLS[selectedSymbol.toUpperCase()].name, lotSize: _CDS_SYMBOLS[selectedSymbol.toUpperCase()].lotSize, exchange: "CDS", derivExchange: "CDS", category: "Currency" } : null)
    || (_MCX_LOT[selectedSymbol.toUpperCase()] ? { symbol: selectedSymbol.toUpperCase(), name: selectedSymbol.toUpperCase(), lotSize: _MCX_LOT[selectedSymbol.toUpperCase()], exchange: "MCX", derivExchange: "MCX", category: "Commodity" } : null)
    || fnoInstruments[0];

  const derivExchange = currentInstrument?.derivExchange || 'NFO';
  const { ticks: strikeTicks, isConnected: strikeWsConnected } = useRealtimeFnO(
    maxCoverage ? strikeTradingSymbols : []
  );

  // Merge individual strike ticks into option chain display
  useEffect(() => {
    if (!maxCoverage || !strikeTicks || Object.keys(strikeTicks).length === 0) return;
    setOptionChain(prev => {
      if (!prev?.strikes) return prev;
      const updatedStrikes = prev.strikes.map(s => {
        const ceTick = s.ce?.tradingsymbol ? strikeTicks[s.ce.tradingsymbol] : null;
        const peTick = s.pe?.tradingsymbol ? strikeTicks[s.pe.tradingsymbol] : null;
        if (!ceTick && !peTick) return s;
        return {
          ...s,
          ce: ceTick ? {
            ...s.ce,
            ltp: ceTick.last_price ?? s.ce?.ltp,
            volume: ceTick.volume_traded ?? ceTick.volume ?? s.ce?.volume,
            oi: ceTick.oi ?? s.ce?.oi,
            change: ceTick.change ?? s.ce?.change,
            change_percent: ceTick.change_percent ?? s.ce?.change_percent,
            bid_price: ceTick.depth?.buy?.[0]?.price ?? s.ce?.bid_price,
            ask_price: ceTick.depth?.sell?.[0]?.price ?? s.ce?.ask_price,
            _live: true,
          } : s.ce,
          pe: peTick ? {
            ...s.pe,
            ltp: peTick.last_price ?? s.pe?.ltp,
            volume: peTick.volume_traded ?? peTick.volume ?? s.pe?.volume,
            oi: peTick.oi ?? s.pe?.oi,
            change: peTick.change ?? s.pe?.change,
            change_percent: peTick.change_percent ?? s.pe?.change_percent,
            bid_price: peTick.depth?.buy?.[0]?.price ?? s.pe?.bid_price,
            ask_price: peTick.depth?.sell?.[0]?.price ?? s.pe?.ask_price,
            _live: true,
          } : s.pe,
        };
      });
      return { ...prev, strikes: updatedStrikes };
    });
  }, [maxCoverage, strikeTicks]);
  // ── End Max Coverage ───────────────────────────────────────
  
  // AI Advisory States
  const [advisoryLoading, setAdvisoryLoading] = useState(false);
  const [advisoryData, setAdvisoryData] = useState(null);
  const [advisoryExpanded, setAdvisoryExpanded] = useState({
    institutional: true,
    optionChain: true,
    greeks: true,
    market: false,
    emotional: false
  });
  
  // AI Chatbot States
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(`options_chat_${Date.now()}`);
  const chatEndRef = useRef(null);

  // Filter instruments based on search
  const filteredInstruments = fnoInstruments.filter(
    (inst) =>
      inst.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inst.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    // Only fetch option chain after expiry dates are loaded — initial one-shot via useRestOnce below
    if (expiryDates.length === 0) return;
    // Nothing to do here; initial load + balance handled by useRestOnce hooks.
  }, [selectedSymbol, selectedExpiry, expiryDates.length]);

  // Build the option-chain REST path dynamically (changes when symbol/expiry changes)
  const ocPath = useMemo(() => {
    if (expiryDates.length === 0) return null;
    let path = `/market/optionchain/${selectedSymbol}`;
    if (selectedExpiry > 0 && expiryDates[selectedExpiry]) {
      const expiryParam = expiryDates[selectedExpiry]?.nseFormat || expiryDates[selectedExpiry]?.fullLabel || '';
      if (expiryParam) path += `?expiry=${encodeURIComponent(expiryParam)}`;
    }
    return path;
  }, [selectedSymbol, selectedExpiry, expiryDates]);

  // One-shot initial load — no polling (WS stream handles live updates)
  const { data: ocInitialData, loading: ocRestLoading } = useRestOnce(ocPath, {
    skip: !ocPath,
    cacheKey: `oc-chain-${selectedSymbol}-${selectedExpiry}`,
    ttl: 60_000,
  });

  // Seed optionChain state from the initial REST snapshot
  useEffect(() => {
    if (ocInitialData?.strikes?.length > 0) {
      setOptionChain(ocInitialData);
      setLoading(false);
    } else if (ocInitialData !== null && ocInitialData !== undefined) {
      setLoading(false);
    }
  }, [ocInitialData]);

  // Safety net: if OC REST fetch finished but ocInitialData stayed null (error), clear loading
  useEffect(() => {
    if (ocPath && !ocRestLoading && ocInitialData === null) {
      setLoading(false);
    }
  }, [ocPath, ocRestLoading, ocInitialData]);

  // Auto-scroll to ATM strike after data loads
  useEffect(() => {
    if (!optionChain?.strikes?.length || hasScrolledToATM.current) return;
    // Slight delay to ensure DOM is rendered
    const timer = setTimeout(() => {
      if (atmRowRef.current && ocScrollRef.current) {
        const container = ocScrollRef.current;
        const row = atmRowRef.current;
        const rowTop = row.offsetTop - container.offsetTop;
        const scrollTarget = rowTop - container.clientHeight / 2 + row.clientHeight / 2;
        container.scrollTop = Math.max(0, scrollTarget);
        hasScrolledToATM.current = true;
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [optionChain?.strikes?.length]);

  // User balance via useRestOnce (no polling)
  const { data: balanceData, refresh: refreshBalance } = useRestOnce('/portfolio', {
    cacheKey: 'oc-portfolio-balance',
  });

  useEffect(() => {
    if (balanceData?.virtual_balance != null) {
      setUserBalance(balanceData.virtual_balance);
    }
  }, [balanceData]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // fetchOptionChain kept only for manual retry button (no polling)
  const fetchOptionChain = async (symbol, expiryIndex = 0, isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      
      const params = {};
      const selectedExpiryData = expiryDates[expiryIndex];
      if (expiryIndex > 0 && selectedExpiryData) {
        const expiryParam = selectedExpiryData?.nseFormat || selectedExpiryData?.fullLabel || '';
        if (expiryParam) params.expiry = expiryParam;
      }
      
      const response = await axios.get(`${API}/market/optionchain/${symbol}`, { params });
      
      const data = response.data;
      if (data?.strikes?.length > 0 || isInitialLoad) {
        setOptionChain(data);
      }
    } catch (error) {
      console.error("Failed to load option chain");
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  const handleSymbolChange = (symbol) => {
    setSelectedSymbol(symbol);
    setSearchParams({ symbol });
    setShowInstrumentDropdown(false);
    // Expiry dates will auto-refresh via useRestOnce when selectedSymbol changes
    setSelectedExpiry(0); // Reset to first expiry
    setLoading(true); // Show loading until new data arrives
    hasScrolledToATM.current = false; // Reset scroll for new symbol
  };

  const handleAIStrategyCreate = async () => {
    if (!strategyText.trim()) {
      toast.error("Please enter your trading strategy");
      return;
    }

    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/create-strategy`, {
        strategy_text: strategyText,
        spot_price: optionChain?.spot_price,
      });
      
      setAiStrategy(response.data.strategy);
      setConfirmDialog(true);
      toast.success("AI created your strategy successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to parse strategy");
    } finally {
      setAiLoading(false);
    }
  };

  const handleExecuteStrategy = async () => {
    try {
      for (const leg of aiStrategy.legs) {
        await axios.post(`${API}/orders`, {
          segment: "fo",
          symbol: `${leg.symbol}${leg.strike}${leg.option_type}`,
          name: `${leg.symbol} ${leg.strike} ${leg.option_type}`,
          order_type: leg.action.toLowerCase(),
          quantity: leg.quantity,
          price: leg.premium,
        });
      }
      
      toast.success(`${aiStrategy.strategy_name} executed successfully!`);
      setConfirmDialog(false);
      setStrategyText("");
      setAiStrategy(null);
    } catch (error) {
      toast.error("Failed to execute strategy");
    }
  };

  // Handle Buy/Sell button click - Opens Order Panel with AI Advisory
  const handleTradeClick = async (strike, optionType, action, premium, greeks) => {
    setSelectedStrike(strike);
    setSelectedOptionType(optionType);
    setOrderAction(action);
    setOrderPrice(premium);
    setOrderQuantity(1);
    setOrderPanelOpen(true);
    setAdvisoryData(null);
    setAdvisoryLoading(true);
    
    // Fetch AI Advisory using canonical endpoint
    const tradeData = {
      symbol: selectedSymbol,
      segment: "options",
      exchange: currentInstrument?.derivExchange || "NFO",
      strike: strike.strike,
      option_type: optionType,
      ltp: premium,
      spot_price: optionChain?.spot_price,
      lot_size: currentInstrument.lotSize,
      greeks: greeks || {},
      oi: optionType === "CE" ? strike.ce?.oi : strike.pe?.oi,
      oi_change: optionType === "CE" ? strike.ce?.oi_change : strike.pe?.oi_change,
      volume: optionType === "CE" ? strike.ce?.volume : strike.pe?.volume,
      change: optionType === "CE" ? strike.ce?.change : strike.pe?.change,
      change_pct: optionType === "CE" ? strike.ce?.change_pct : strike.pe?.change_pct,
      iv: greeks?.iv,
      expiry: wsExpiry || "",
      market_status: "open",
    };
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/advisory/analyze`, tradeData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdvisoryData(response.data?.advisory || null);
    } catch (error) {
      console.error("Failed to get AI advisory");
      setAdvisoryData(null);
    } finally {
      setAdvisoryLoading(false);
    }
  };

  // Execute trade from order panel
  const handleExecuteTrade = async () => {
    if (!selectedStrike) return;
    
    const totalCost = orderPrice * orderQuantity * currentInstrument.lotSize;
    
    try {
      await axios.post(`${API}/orders`, {
        segment: "fo",
        symbol: `${selectedSymbol}${selectedStrike.strike}${selectedOptionType}`,
        name: `${selectedSymbol} ${selectedStrike.strike} ${selectedOptionType}`,
        order_type: orderAction.toLowerCase(),
        quantity: orderQuantity * currentInstrument.lotSize,
        price: orderPrice,
      });
      
      toast.success(`${orderAction} order executed successfully!`);
      setOrderPanelOpen(false);
      refreshBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to execute order");
    }
  };

  // AI Chatbot message handler
  const handleChatSend = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setChatInput("");
    setChatLoading(true);
    
    try {
      const response = await axios.post(`${API}/ai/options-chat`, {
        message: userMessage,
        session_id: sessionId,
        context: {
          symbol: selectedSymbol,
          spot_price: optionChain?.spot_price
        }
      });
      
      setChatMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Sorry, I couldn't process your request. Please try again." 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Dynamic quick strategies
  const quickStrategies = [
    `Long straddle on ${selectedSymbol} at current price`,
    `Short strangle on ${selectedSymbol}`,
    `Bull call spread on ${selectedSymbol}`,
    `Iron condor on ${selectedSymbol}`,
    `Bear put spread on ${selectedSymbol}`,
  ];

  // Basket -> Build Strategy
  const basketEstimatedPremium = useMemo(() => {
    return basketItems.reduce((sum, item) => {
      const premium = item.ltp || 0;
      return sum + (item.side === "BUY" ? premium : -premium) * item.qty;
    }, 0);
  }, [basketItems]);

  const buildStrategyFromBasket = async () => {
    if (basketItems.length === 0) return;
    setBuildingStrategy(true);
    try {
      const expiryStr = wsExpiry || new Date().toISOString().split('T')[0];
      const legs = basketItems.map(item => ({
        option_type: item.option_type,
        strike: item.strike,
        side: item.side,
        qty: item.qty,
      }));
      const res = await axios.post(`${API}/strategies/from-basket`, {
        underlying: selectedSymbol,
        expiry: expiryStr,
        legs,
      });
      if (res.data?.ok) {
        toast.success(`Strategy created with ${legs.length} legs`);
        navigate(`/strategy-review/${res.data.strategy_id}`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error_code) {
        toast.error(`${detail.error_code}: ${detail.message}`);
      } else {
        toast.error("Failed to create strategy");
      }
    } finally {
      setBuildingStrategy(false);
    }
  };

  // StoxCap Pro: Compute max OI (in lots) for progress bar scaling
  const maxCallOI = useMemo(() => {
    if (!optionChain?.strikes) return 1;
    return Math.max(1, ...optionChain.strikes.map(s => {
      const oi = s.ce?.oi_lots ?? (s.ce?.oi && s.ce?.lot_size ? Math.round(s.ce.oi / s.ce.lot_size) : (s.ce?.oi || 0));
      return oi;
    }));
  }, [optionChain]);
  const maxPutOI = useMemo(() => {
    if (!optionChain?.strikes) return 1;
    return Math.max(1, ...optionChain.strikes.map(s => {
      const oi = s.pe?.oi_lots ?? (s.pe?.oi && s.pe?.lot_size ? Math.round(s.pe.oi / s.pe.lot_size) : (s.pe?.oi || 0));
      return oi;
    }));
  }, [optionChain]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen font-manrope">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#00C896] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <div className="text-lg font-semibold text-gray-500">Loading option chain...</div>
        </div>
      </div>
    );
  }

  // Risk level colors
  const getRiskColor = (level) => {
    switch(level) {
      case 'LOW': return 'text-green-600 bg-green-100 border-green-300';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-100 border-yellow-300';
      case 'HIGH': return 'text-red-600 bg-red-100 border-red-300';
      default: return 'text-gray-600 bg-gray-100 border-gray-300';
    }
  };

  const getStanceColor = (rec) => {
    switch(rec) {
      case 'PROCEED': return 'text-emerald-700 bg-green-100 border-green-300';
      case 'CAUTION': return 'text-yellow-700 bg-yellow-100 border-yellow-300';
      case 'AVOID': return 'text-rose-700 bg-red-100 border-red-300';
      default: return 'text-gray-700 bg-gray-100 border-gray-300';
    }
  };

  const getSentimentColor = (sentiment) => {
    switch(sentiment) {
      case 'BULLISH': return 'text-green-600';
      case 'BEARISH': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStanceIcon = (stance) => {
    switch(stance) {
      case 'BUYING': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'SELLING': return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <>
      <SEOHead {...PageSEO.optionChain} />
    <div className="space-y-4 max-w-screen-2xl mx-auto relative font-manrope">
      {/* WebSocket Connection Banner */}
      <ConnectionBanner isConnected={isLiveConnected} streamStatus={isLiveConnected ? 'connected' : 'reconnecting'} />
      {/* ═══ AI Strategy Builder — Compact ═══ */}
      <Card className="p-4 sm:p-5 rounded-2xl strategy-mesh-bg border border-slate-200/60 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00C896] to-emerald-600 flex items-center justify-center shadow-sm">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 font-manrope">AI Strategy Builder</h2>
            <p className="text-xs text-slate-500">Build options strategies for <span className="font-semibold text-[#00C896]">{currentInstrument.name}</span></p>
          </div>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder={`e.g., Long straddle on ${selectedSymbol} ${optionChain?.spot_price || 24250}`}
            value={strategyText}
            onChange={(e) => setStrategyText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAIStrategyCreate()}
            data-testid="oc-strategy-input"
            className="flex-1 h-11 rounded-lg border-gray-200 focus:border-[#00C896] text-sm"
          />
          <Button
            onClick={handleAIStrategyCreate}
            disabled={aiLoading}
            data-testid="oc-build-strategy-btn"
            className="h-11 px-6 rounded-lg bg-[#00C896] hover:bg-[#00b386] text-white font-semibold text-sm"
          >
            {aiLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="hidden sm:inline">Analyzing...</span>
              </div>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">Build</span>
              </>
            )}
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <span className="text-xs text-gray-400">Popular:</span>
          {quickStrategies.slice(0, 3).map((strategy, index) => (
            <button key={index} onClick={() => setStrategyText(strategy)} data-testid={`oc-quick-strategy-${index}`}
              className="px-2.5 py-1 text-xs bg-white border border-gray-200 rounded-full hover:bg-gray-50 hover:border-[#00C896]/40 transition-colors text-gray-600">
              {strategy}
            </button>
          ))}
        </div>
      </Card>

      {/* ═══ Option Chain Header ═══ */}
      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <h2 className="text-base font-bold text-gray-900 font-manrope">Option Chain</h2>

            {/* Instrument Selector */}
            <div className="relative">
              <button onClick={() => setShowInstrumentDropdown(!showInstrumentDropdown)} data-testid="instrument-selector-btn"
                className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                <span className="font-bold text-gray-800 text-sm">{selectedSymbol}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${showInstrumentDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showInstrumentDropdown && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden max-h-[400px]">
                  <div className="p-2 border-b border-gray-100">
                    <input type="text" placeholder="Search instruments..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#00C896]"
                      data-testid="instrument-search-input" />
                  </div>
                  <div className="py-1 overflow-y-auto max-h-[340px]">
                    {filteredInstruments.length > 0 ? (<>
                      {filteredInstruments.filter(i => i.category === 'Index').length > 0 && (
                        <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Indices</div>
                      )}
                      {filteredInstruments.filter(i => i.category === 'Index').map((inst) => (
                        <button key={inst.symbol} onClick={() => { handleSymbolChange(inst.symbol); setShowInstrumentDropdown(false); setSearchQuery(''); }}
                          data-testid={`instrument-dropdown-${inst.symbol}`}
                          className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors">
                          <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${selectedSymbol === inst.symbol ? 'border-[#00C896]' : 'border-gray-300'}`}>
                            {selectedSymbol === inst.symbol && <div className="w-1.5 h-1.5 rounded-full bg-[#00C896]" />}
                          </div>
                          <div className="text-left">
                            <span className={`text-sm font-medium ${selectedSymbol === inst.symbol ? 'text-gray-900' : 'text-gray-700'}`}>{inst.symbol}</span>
                            <span className="text-[10px] text-gray-400 ml-1.5">Lot: {inst.lotSize}</span>
                          </div>
                        </button>
                      ))}
                      {filteredInstruments.filter(i => i.category === 'Stock').length > 0 && (
                        <div className="px-3 py-1 mt-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-t border-gray-100">Stocks</div>
                      )}
                      {filteredInstruments.filter(i => i.category === 'Stock').map((inst) => (
                        <button key={inst.symbol} onClick={() => { handleSymbolChange(inst.symbol); setShowInstrumentDropdown(false); setSearchQuery(''); }}
                          data-testid={`instrument-dropdown-${inst.symbol}`}
                          className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors">
                          <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${selectedSymbol === inst.symbol ? 'border-[#00C896]' : 'border-gray-300'}`}>
                            {selectedSymbol === inst.symbol && <div className="w-1.5 h-1.5 rounded-full bg-[#00C896]" />}
                          </div>
                          <div className="text-left">
                            <span className={`text-sm font-medium ${selectedSymbol === inst.symbol ? 'text-gray-900' : 'text-gray-700'}`}>{inst.symbol}</span>
                            <span className="text-[10px] text-gray-400 ml-1.5">{inst.name}</span>
                          </div>
                        </button>
                      ))}
                    </>) : (
                      <div className="px-4 py-3 text-sm text-gray-400">No instruments found</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Expiry Selector */}
            <div className="relative flex items-center gap-2">
              <button onClick={() => setShowExpiryDropdown(!showExpiryDropdown)}
                className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors" data-testid="expiry-selector-btn">
                <span className="font-bold text-gray-800 text-sm">{expiryDates[selectedExpiry]?.label}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${showExpiryDropdown ? 'rotate-180' : ''}`} />
              </button>
              {selectedExpiry === 0 && expiryDates.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] text-[#00C896] bg-[#00C896]/10 px-2 py-0.5 rounded-full whitespace-nowrap" data-testid="nearest-expiry-badge">
                  Nearest: {expiryDates[0]?.label}
                </span>
              )}
              {showExpiryDropdown && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden max-h-72 overflow-y-auto">
                  <div className="py-2">
                    <p className="text-xs text-gray-500 px-4 pb-2 font-medium border-b border-gray-100 mb-2">Select Expiry Date</p>
                    {expiryDates.map((expiry, index) => (
                      <button key={index} onClick={() => { setSelectedExpiry(index); setShowExpiryDropdown(false); }}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${selectedExpiry === index ? 'bg-[#00C896]/10' : ''}`}>
                        <div>
                          <span className={`text-sm font-medium ${selectedExpiry === index ? 'text-[#00C896]' : 'text-gray-700'}`}>{expiry.fullLabel}</span>
                          <p className="text-xs text-gray-500">{expiry.daysToExpiry}d to expiry</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${expiry.isMonthly ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                          {expiry.isMonthly ? 'Monthly' : 'Weekly'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Spot Price + Actions */}
          <div className="flex items-center gap-3">
            <div className={`transition-all duration-200 ${spotFlash ? 'ring-2 ring-[#00C896]/50 rounded-lg px-2 py-1' : ''}`}>
              <p className="text-sm text-gray-600">
                Spot: <span className={`font-bold font-mono-num transition-colors duration-200 ${spotFlash ? 'text-[#00C896]' : 'text-gray-900'}`}>
                  ₹{(liveSpotPrice || optionChain?.spot_price)?.toLocaleString()}
                </span>
                {liveSpotChange !== undefined && (
                  <span className={`ml-1.5 text-xs font-semibold font-mono-num ${liveSpotChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ({liveSpotChange >= 0 ? '+' : ''}{liveSpotChange?.toFixed(2)}%)
                  </span>
                )}
              </p>
            </div>
            <button onClick={() => { setAlertPrice((liveSpotPrice || optionChain?.spot_price || "").toString()); setShowAlertDialog(true); }}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200 transition-colors"
              data-testid="oc-create-alert-btn">
              <Bell className="w-3 h-3" /> Alert
            </button>
            <button onClick={handleWatchlistToggle} disabled={wlLoading}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${inWatchlist ? 'bg-yellow-50 text-yellow-700 border-yellow-300' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
              data-testid="oc-watchlist-btn">
              <Star className={`w-3 h-3 ${inWatchlist ? 'fill-yellow-400' : ''}`} /> {inWatchlist ? 'Watching' : 'Watch'}
            </button>
          </div>
        </div>
      </div>

      {/* Integrity Banner */}
      {(() => {
        const integ = wsIntegrity || optionChain?.integrity;
        if (!integ) return null;
        const afterEnrich = integ.missing_greeks_pct_after_enrich ?? integ.missing_greeks_pct ?? 0;
        const showBanner = (integ.missing_iv_pct > 15 || afterEnrich > 50);
        if (!showBanner) return null;
        const parts = [];
        if (integ.missing_iv_pct > 15) parts.push(`IV ${integ.missing_iv_pct.toFixed(1)}% missing`);
        if (afterEnrich > 50) parts.push(`Greeks partially available (${(100 - afterEnrich).toFixed(0)}% computed)`);
        return (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm" data-testid="integrity-banner">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{parts.join('. ')}.</span>
          </div>
        );
      })()}

      {/* ═══ View Controls ═══ */}
      <div className="flex items-center gap-2 px-1 flex-wrap" data-testid="oc-view-controls">
        <button onClick={() => setShowGreeks(!showGreeks)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${showGreeks ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          data-testid="greeks-toggle">
          Greeks {showGreeks ? 'ON' : 'OFF'}
        </button>
        <button onClick={() => setShowDetails(!showDetails)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${showDetails ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          data-testid="details-toggle">
          <BarChart3 className="w-3.5 h-3.5" /> Details {showDetails ? 'ON' : 'OFF'}
        </button>
        <button onClick={() => { setBasketMode(!basketMode); if (basketMode) setBasketItems([]); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${basketMode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          data-testid="basket-toggle">
          Basket {basketMode ? `(${basketItems.length})` : ''}
        </button>
        <button onClick={() => setShowHeatmap(!showHeatmap)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${showHeatmap ? 'bg-[#00C896] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          data-testid="heatmap-toggle">
          <Activity className="w-3.5 h-3.5" /> Heatmap
        </button>
        <button onClick={() => setMaxCoverage(!maxCoverage)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${maxCoverage ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          title={`Max Coverage: Subscribe ${MAX_COVERAGE_RANGE * 2} strikes around ATM for real-time WS ticks`}
          data-testid="max-coverage-toggle">
          <Zap className="w-3.5 h-3.5" /> {maxCoverage ? `Live (${strikeTradingSymbols.length})` : 'Max Coverage'}
          {maxCoverage && strikeWsConnected && <LiveDot isConnected={true} size="xs" />}
        </button>
        {showHeatmap && (
          <div className="flex items-center gap-1">
            {["delta", "gamma", "theta", "vega"].map(g => (
              <button key={g} onClick={() => setHeatmapGreek(g)}
                className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${heatmapGreek === g ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                data-testid={`heatmap-greek-${g}`}>
                {g === "delta" ? "Δ" : g === "gamma" ? "Γ" : g === "theta" ? "Θ" : "V"}
              </button>
            ))}
          </div>
        )}
        {showGreeks && (
          <span className="text-[9px] text-gray-400 ml-auto hidden sm:inline" title="Greeks are computed server-side via Black-Scholes when feed data is unavailable">
            Greeks: server-computed where feed unavailable
          </span>
        )}
      </div>

      {/* ═══ Mobile View Switcher ═══ */}
      <div className="flex sm:hidden bg-slate-100 rounded-xl mx-3 my-2 p-1" data-testid="mobile-view-switcher">
        {['calls', 'full', 'puts'].map(view => (
          <button key={view} onClick={() => setMobileView(view)}
            data-testid={`mobile-view-${view}`}
            className={`oc-view-tab flex-1 py-2 text-xs font-bold uppercase tracking-wider text-center ${mobileView === view ? 'active text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}>
            {view === 'calls' ? 'Calls' : view === 'puts' ? 'Puts' : 'Full'}
          </button>
        ))}
      </div>

      {/* ═══ Greeks Heatmap View ═══ */}
      {showHeatmap && optionChain?.strikes?.length > 0 && (() => {
        const strikes = optionChain.strikes;
        const getVal = (leg, greek) => {
          if (!leg) return null;
          const v = leg[greek] ?? leg[greek];
          return (v !== null && v !== undefined) ? parseFloat(v) : null;
        };
        const allVals = strikes.flatMap(s => [getVal(s.ce, heatmapGreek), getVal(s.pe, heatmapGreek)]).filter(v => v !== null);
        const maxAbs = Math.max(...allVals.map(Math.abs), 0.001);

        const heatStyle = (val) => {
          if (val === null) return { backgroundColor: '#f3f4f6' };
          const ratio = Math.min(Math.abs(val) / maxAbs, 1);
          const alpha = Math.max(0.05, ratio * 0.6);
          if (heatmapGreek === "theta") return { backgroundColor: `rgba(239,68,68,${alpha})` };
          if (val >= 0) return { backgroundColor: `rgba(16,185,129,${alpha})` };
          return { backgroundColor: `rgba(239,68,68,${alpha})` };
        };

        return (
          <Card className="rounded-2xl overflow-hidden" data-testid="greeks-heatmap">
            <div className="px-4 py-2 bg-gray-50 border-b flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">
                Greeks Heatmap: {heatmapGreek === "delta" ? "Delta (Δ)" : heatmapGreek === "gamma" ? "Gamma (Γ)" : heatmapGreek === "theta" ? "Theta (Θ)" : "Vega (V)"}
              </span>
              <div className="flex items-center gap-2 text-[9px] text-gray-400">
                <span className="flex items-center gap-1"><span className="w-3 h-2 rounded" style={{backgroundColor:'rgba(16,185,129,0.6)'}}></span>High +</span>
                <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-gray-200"></span>Neutral</span>
                <span className="flex items-center gap-1"><span className="w-3 h-2 rounded" style={{backgroundColor:'rgba(239,68,68,0.6)'}}></span>High -</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[10px] font-mono-num">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="py-2 px-3 text-center font-bold text-emerald-700 bg-green-50">CE {heatmapGreek}</th>
                    <th className="py-2 px-3 text-center font-bold text-gray-700">Strike</th>
                    <th className="py-2 px-3 text-center font-bold text-rose-700 bg-red-50">PE {heatmapGreek}</th>
                  </tr>
                </thead>
                <tbody>
                  {strikes.map(s => {
                    const ceVal = getVal(s.ce, heatmapGreek);
                    const peVal = getVal(s.pe, heatmapGreek);
                    const isATM = Math.abs(s.strike - optionChain.spot_price) < 100;
                    return (
                      <tr key={s.strike} className={isATM ? "font-bold" : ""}>
                        <td className="py-1.5 px-3 text-center" style={heatStyle(ceVal)}>
                          {ceVal !== null ? ceVal.toFixed(heatmapGreek === "gamma" ? 4 : 2) : "—"}
                        </td>
                        <td className={`py-1.5 px-3 text-center ${isATM ? 'bg-yellow-100 font-bold' : 'bg-gray-50'}`}>
                          {s.strike.toLocaleString()}
                        </td>
                        <td className="py-1.5 px-3 text-center" style={heatStyle(peVal)}>
                          {peVal !== null ? peVal.toFixed(heatmapGreek === "gamma" ? 4 : 2) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════
           StoxCap Pro — Option Chain Table (Groww-style)
           ═══════════════════════════════════════════════════════════ */}
      {/* Stale data warning banner */}
      {optionChain?.stale_warning && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-3 flex items-center gap-3" data-testid="oc-stale-warning">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Option chain data is stale</p>
            <p className="text-xs text-amber-600">{optionChain.stale_warning}</p>
          </div>
        </div>
      )}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="option-chain-table-card">
        <div className="overflow-auto max-h-[76vh]" data-testid="option-chain-scroll-container" ref={ocScrollRef}>
          <table className="w-full text-[11px] oc-table" data-testid="option-chain-table">
            {/* ── Sticky Header ── */}
            <thead className="oc-glass-header sticky top-0 z-20" data-testid="option-chain-sticky-header">
              <tr className="border-b border-gray-200">
                {/* CALL SIDE — order: far details → Greeks → OI → LTP → (Strike) */}
                {(mobileView === 'calls' || mobileView === 'full') && <>
                  {basketMode && <th className="py-2.5 px-1 text-[10px] font-semibold text-gray-400 uppercase bg-white"></th>}
                  <th className="py-2.5 px-1 text-[10px] font-semibold text-gray-500 uppercase bg-white text-center w-[72px] font-manrope">Trade</th>
                  {showDetails && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-white hidden md:table-cell font-manrope">Chg%</th>}
                  {showDetails && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-white hidden md:table-cell font-manrope">Chg</th>}
                  {showDetails && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-white hidden sm:table-cell font-manrope">IV%</th>}
                  {showDetails && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-white hidden sm:table-cell font-manrope">Vol</th>}
                  {showDetails && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-white hidden sm:table-cell font-manrope">OI Chg</th>}
                  {showGreeks && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase bg-white hidden xl:table-cell">Rho</th>}
                  {showGreeks && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase bg-white hidden xl:table-cell">Vega</th>}
                  {showGreeks && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase bg-white hidden lg:table-cell">Theta</th>}
                  {showGreeks && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase bg-white hidden lg:table-cell">Gamma</th>}
                  {showGreeks && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase bg-white hidden lg:table-cell">Delta</th>}
                  <th className="py-2.5 px-2 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-white font-manrope">Call OI</th>
                  <th className="py-2.5 px-2 text-right text-[10px] font-bold text-emerald-600 uppercase tracking-wider bg-white font-manrope">Call LTP</th>
                </>}

                {/* STRIKE — sticky column */}
                <th className="py-2.5 px-3 text-[10px] font-bold text-gray-700 uppercase tracking-wider bg-gray-50 text-center strike-col font-manrope min-w-[72px]">Strike</th>

                {/* PUT SIDE — order: (Strike) → LTP → OI → Greeks → far details */}
                {(mobileView === 'puts' || mobileView === 'full') && <>
                  <th className="py-2.5 px-2 text-right text-[10px] font-bold text-rose-600 uppercase tracking-wider bg-white font-manrope">Put LTP</th>
                  <th className="py-2.5 px-2 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-white font-manrope">Put OI</th>
                  {showGreeks && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase bg-white hidden lg:table-cell">Delta</th>}
                  {showGreeks && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase bg-white hidden lg:table-cell">Gamma</th>}
                  {showGreeks && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase bg-white hidden lg:table-cell">Theta</th>}
                  {showGreeks && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase bg-white hidden xl:table-cell">Vega</th>}
                  {showGreeks && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase bg-white hidden xl:table-cell">Rho</th>}
                  {showDetails && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-white hidden sm:table-cell font-manrope">OI Chg</th>}
                  {showDetails && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-white hidden sm:table-cell font-manrope">Vol</th>}
                  {showDetails && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-white hidden sm:table-cell font-manrope">IV%</th>}
                  {showDetails && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-white hidden md:table-cell font-manrope">Chg</th>}
                  {showDetails && <th className="py-2.5 px-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-white hidden md:table-cell font-manrope">Chg%</th>}
                  <th className="py-2.5 px-1 text-[10px] font-semibold text-gray-500 uppercase bg-white text-center w-[72px] font-manrope">Trade</th>
                  {basketMode && <th className="py-2.5 px-1 text-[10px] font-semibold text-gray-400 uppercase bg-white"></th>}
                </>}
              </tr>
            </thead>

            {/* ── Table Body ── */}
            <tbody>
              {loading && !optionChain?.strikes?.length ? (
                <tr><td colSpan="30" className="py-12 text-center">
                  <div className="animate-spin h-8 w-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-2" />
                  <p className="text-gray-400 text-sm font-manrope">Loading option chain...</p>
                </td></tr>
              ) : !optionChain?.strikes?.length ? (
                <tr><td colSpan="30" className="py-12 text-center" data-testid="option-chain-empty-state">
                  <p className="text-gray-500 font-semibold mb-1 font-manrope">Option chain data currently unavailable</p>
                  <p className="text-gray-400 text-xs mb-3 font-manrope">No snapshot received for {currentInstrument?.name || selectedSymbol}.</p>
                  <button onClick={() => fetchOptionChain(selectedSymbol, selectedExpiry, true)}
                    className="px-4 py-1.5 bg-[#00C896] text-white text-xs font-semibold rounded-lg hover:bg-[#00b386] transition-colors"
                    data-testid="option-chain-retry-btn">
                    Retry
                  </button>
                </td></tr>
              ) : optionChain?.strikes.map((strike, index) => {
                const isATM = Math.abs(strike.strike - optionChain.spot_price) < 100;
                const isITM_CE = strike.strike < optionChain.spot_price;
                const isITM_PE = strike.strike > optionChain.spot_price;

                const spotPrice = liveSpotPrice || optionChain?.spot_price;
                const nextStrike = optionChain.strikes[index + 1];
                const showSpotMarker = nextStrike && spotPrice && strike.strike < spotPrice && nextStrike.strike >= spotPrice;
                const totalCols = 30; // safe max

                // Format helpers — OI displayed in lots (oi_lots from backend)
                const fmtOI = (side) => {
                  const lots = side?.oi_lots;
                  if (lots != null && lots > 0) return Number(lots).toLocaleString('en-IN');
                  const raw = side?.oi;
                  if (raw != null && raw > 0) {
                    const ls = side?.lot_size || 1;
                    return Number(Math.round(raw / ls)).toLocaleString('en-IN');
                  }
                  return '—';
                };
                const fmtExact = (v) => (v != null && v !== 0) ? Number(v).toLocaleString('en-IN') : '—';
                const fmtGreek = (v, dec = 2) => (v != null) ? parseFloat(v).toFixed(dec) : '—';
                const safeIV = (v) => (v != null && v !== 0) ? `${parseFloat(v).toFixed(1)}%` : '—';
                const safeLTP = (v, side) => {
                  if (v != null && v > 0) {
                    return `₹${parseFloat(v).toFixed(2)}`;
                  }
                  return '—';
                };
                const safeChg = (v, ltp) => {
                  if (v == null || ltp == null || ltp === 0) return '';
                  return `${v >= 0 ? '+' : ''}${parseFloat(v).toFixed(2)}%`;
                };
                const safeChgColor = (v) => (v != null && v > 0) ? 'text-green-600 font-semibold' : (v != null && v < 0 ? 'text-red-600 font-semibold' : 'text-gray-400');
                const safeChgAbs = (v, ltp) => {
                  if (v == null || ltp == null || ltp === 0) return '';
                  return `₹${Math.abs(v).toFixed(2)}`;
                };
                const isIlliquid = (side) => {
                  if (!side) return true;
                  // A contract is truly illiquid only if LTP=0 AND no bid/ask AND no OI
                  return (side.ltp === 0 || side.ltp == null) && (side.bid === 0 || !side.bid) && (side.ask === 0 || !side.ask) && (side.oi === 0 || side.oi == null);
                };
                const ceIlliquid = isIlliquid(strike.ce);
                const peIlliquid = isIlliquid(strike.pe);

                // Server Greeks
                const ceDelta = strike.ce?.delta != null ? parseFloat(strike.ce.delta).toFixed(2) : '—';
                const peDelta = strike.pe?.delta != null ? parseFloat(strike.pe.delta).toFixed(2) : '—';
                const ceGamma = strike.ce?.gamma != null ? parseFloat(strike.ce.gamma).toFixed(4) : '—';
                const peGamma = strike.pe?.gamma != null ? parseFloat(strike.pe.gamma).toFixed(4) : '—';
                const ceTheta = strike.ce?.theta != null ? parseFloat(strike.ce.theta).toFixed(2) : '—';
                const peTheta = strike.pe?.theta != null ? parseFloat(strike.pe.theta).toFixed(2) : '—';
                const ceVega = strike.ce?.vega != null ? parseFloat(strike.ce.vega).toFixed(2) : '—';
                const peVega = strike.pe?.vega != null ? parseFloat(strike.pe.vega).toFixed(2) : '—';
                const ceRho = fmtGreek(strike.ce?.rho, 4);
                const peRho = fmtGreek(strike.pe?.rho, 4);

                const ceGreeks = { delta: ceDelta, gamma: ceGamma, theta: ceTheta, vega: ceVega, iv: strike.ce?.iv };
                const peGreeks = { delta: peDelta, gamma: peGamma, theta: peTheta, vega: peVega, iv: strike.pe?.iv };

                // Basket
                const basketKey_CE = `${strike.strike}-CE`;
                const basketKey_PE = `${strike.strike}-PE`;
                const basketItem_CE = basketItems.find(x => x.key === basketKey_CE);
                const basketItem_PE = basketItems.find(x => x.key === basketKey_PE);
                const isInBasket_CE = !!basketItem_CE;
                const isInBasket_PE = !!basketItem_PE;
                const toggleBasketCheck = (key, optType, side = "BUY") => {
                  const illiq = optType === "CE" ? ceIlliquid : peIlliquid;
                  if (illiq) return;
                  setBasketItems(prev => {
                    const existing = prev.find(x => x.key === key);
                    if (existing) {
                      if (existing.side === side) return prev.filter(x => x.key !== key);
                      return prev.map(x => x.key === key ? { ...x, side } : x);
                    }
                    return [...prev, { key, strike: strike.strike, option_type: optType, side, qty: currentInstrument.lotSize, ltp: optType === "CE" ? strike.ce?.ltp : strike.pe?.ltp }];
                  });
                };

                // OI bar width percentages (using lots)
                const ceOIlots = strike.ce?.oi_lots ?? (strike.ce?.oi && strike.ce?.lot_size ? Math.round(strike.ce.oi / strike.ce.lot_size) : (strike.ce?.oi || 0));
                const peOIlots = strike.pe?.oi_lots ?? (strike.pe?.oi && strike.pe?.lot_size ? Math.round(strike.pe.oi / strike.pe.lot_size) : (strike.pe?.oi || 0));
                const ceOIpct = ceOIlots ? Math.min(100, (ceOIlots / maxCallOI) * 100) : 0;
                const peOIpct = peOIlots ? Math.min(100, (peOIlots / maxPutOI) * 100) : 0;

                // ITM tint classes
                const ceTint = isITM_CE ? 'itm-call-tint' : '';
                const peTint = isITM_PE ? 'itm-put-tint' : '';

                return (
                  <React.Fragment key={strike.strike}>
                  <tr ref={isATM ? atmRowRef : undefined} className={`oc-row group border-b border-gray-100 hover:bg-gray-50 transition-colors ${isATM ? 'oc-atm-row' : ''}`} data-testid={`oc-row-${strike.strike}`}>

                    {/* ═══ CALL SIDE — order: Basket → Trade → Details → Greeks → OI → LTP ═══ */}
                    {(mobileView === 'calls' || mobileView === 'full') && <>
                      {/* CE Basket checkbox */}
                      {basketMode && <td className={`py-1 px-1 ${ceTint}`}>
                        <div className="flex items-center justify-center" title={ceIlliquid ? "Basket not allowed for illiquid contracts" : isInBasket_CE ? `${basketItem_CE.side} selected` : "Click B or S"}>
                          {isInBasket_CE ? (
                            <button onClick={() => toggleBasketCheck(basketKey_CE, "CE", basketItem_CE.side)}
                              className={`w-4 h-4 rounded text-[9px] font-bold flex items-center justify-center ${basketItem_CE.side === 'BUY' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}
                              data-testid={`basket-check-ce-${strike.strike}`}>{basketItem_CE.side === 'BUY' ? 'B' : 'S'}</button>
                          ) : (
                            <div className={`w-4 h-4 rounded border-2 ${ceIlliquid ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-40' : 'border-gray-300'}`} data-testid={`basket-check-ce-${strike.strike}`} />
                          )}
                        </div>
                      </td>}

                      {/* CE Trade buttons */}
                      <td className={`py-1 px-1 ${ceTint}`}>
                        <div className="flex gap-0.5 justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => { e.stopPropagation(); const ts = strike.ce?.tradingsymbol; if (ts) { navigate(`/options/${selectedSymbol}/${ts}?exchange=${currentInstrument?.derivExchange || 'NFO'}`); } }}
                            className="h-5 w-5 rounded bg-gray-100 hover:bg-blue-100 text-gray-500 hover:text-blue-600 flex items-center justify-center transition-colors" title={`Contract: ${strike.ce?.tradingsymbol || selectedSymbol + ' ' + strike.strike + ' CE'}`} data-testid={`contract-ce-${strike.strike}`}>
                            <ExternalLink className="w-3 h-3" />
                          </button>
                          <button onClick={() => basketMode ? toggleBasketCheck(basketKey_CE, "CE", "SELL") : handleTradeClick(strike, "CE", "SELL", strike.ce?.ltp, ceGreeks)}
                            className="w-5 h-5 rounded bg-red-500 hover:bg-red-600 text-white text-[9px] font-bold flex items-center justify-center transition-colors" title="Sell" data-testid={`sell-ce-${strike.strike}`}>S</button>
                          <button onClick={() => basketMode ? toggleBasketCheck(basketKey_CE, "CE", "BUY") : handleTradeClick(strike, "CE", "BUY", strike.ce?.ltp, ceGreeks)}
                            className="w-5 h-5 rounded bg-green-500 hover:bg-green-600 text-white text-[9px] font-bold flex items-center justify-center transition-colors" title="Buy" data-testid={`buy-ce-${strike.strike}`}>B</button>
                        </div>
                      </td>

                      {showDetails && <td className={`py-2 px-1.5 text-[11px] text-right hidden md:table-cell ${ceTint}`}><span className={`font-mono-num ${safeChgColor(strike.ce?.change_pct)}`}>{safeChg(strike.ce?.change_pct, strike.ce?.ltp)}</span></td>}
                      {showDetails && <td className={`py-2 px-1.5 text-[11px] text-right hidden md:table-cell ${ceTint}`}><span className={`font-mono-num ${safeChgColor(strike.ce?.change)}`}>{safeChgAbs(strike.ce?.change, strike.ce?.ltp)}</span></td>}
                      {showDetails && <td className={`py-2 px-1.5 text-[11px] text-right font-mono-num hidden sm:table-cell ${ceTint}`}>{safeIV(strike.ce?.iv)}</td>}
                      {showDetails && <td className={`py-2 px-1.5 text-[11px] text-right font-mono-num hidden sm:table-cell ${ceTint}`}>{fmtExact(strike.ce?.volume)}</td>}
                      {showDetails && <td className={`py-2 px-1.5 text-[11px] text-right font-mono-num hidden sm:table-cell ${ceTint}`}>{strike.ce?.oi_change != null ? fmtExact(strike.ce.oi_change) : '—'}</td>}
                      {showGreeks && <td className={`py-2 px-1.5 text-[11px] text-right text-gray-400 font-mono-num hidden xl:table-cell ${ceTint}`}>{ceRho}</td>}
                      {showGreeks && <td className={`py-2 px-1.5 text-[11px] text-right text-gray-500 font-mono-num hidden xl:table-cell ${ceTint}`}>{ceVega}</td>}
                      {showGreeks && <td className={`py-2 px-1.5 text-[11px] text-right text-red-500 font-mono-num hidden lg:table-cell ${ceTint}`}>{ceTheta}</td>}
                      {showGreeks && <td className={`py-2 px-1.5 text-[11px] text-right text-gray-500 font-mono-num hidden lg:table-cell ${ceTint}`}>{ceGamma}</td>}
                      {showGreeks && <td className={`py-2 px-1.5 text-[11px] text-right text-blue-600 font-mono-num hidden lg:table-cell ${ceTint}`}>{ceDelta}</td>}

                      {/* Call OI with bar — adjacent to strike */}
                      <td className={`py-2 px-2 text-right oi-bar-cell ${ceTint}`}>
                        <div className="oi-bar-fill call" style={{ width: `${ceOIpct}%` }} />
                        <div className="relative z-10">
                          <div className="text-[12px] font-medium font-mono-num text-gray-800">{fmtOI(strike.ce)}</div>
                          <div className={`text-[10px] font-mono-num ${strike.ce?.oi_change_pct > 0 ? 'text-green-600' : strike.ce?.oi_change_pct < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                            {strike.ce?.oi_change_pct != null ? `${strike.ce.oi_change_pct >= 0 ? '+' : ''}${parseFloat(strike.ce.oi_change_pct).toFixed(1)}%` : ''}
                          </div>
                        </div>
                      </td>

                      {/* Call LTP — closest to strike */}
                      <td className={`py-2 px-2 text-right ${ceTint}`}>
                        <div className={`font-semibold font-mono-num text-[13px] ${isITM_CE ? 'text-emerald-700' : 'text-gray-900'}`}>{safeLTP(strike.ce?.ltp, strike.ce)}</div>
                        {(() => { const txt = safeChg(strike.ce?.change_pct, strike.ce?.ltp); return txt ? (
                          <div className={`text-[10px] font-mono-num ${strike.ce?.change_pct > 0 ? 'text-green-600' : strike.ce?.change_pct < 0 ? 'text-red-500' : 'text-gray-400'}`}>{txt}</div>
                        ) : null; })()}
                      </td>
                    </>}

                    {/* ═══ STRIKE — sticky middle column ═══ */}
                    <td className={`py-2 px-2 text-center font-bold text-[13px] font-mono-num strike-col ${isATM ? 'bg-amber-100 text-amber-900' : 'bg-gray-50 text-gray-900'}`} data-testid={`strike-${strike.strike}`}>
                      {strike.strike.toLocaleString()}
                    </td>

                    {/* ═══ PUT SIDE — order: LTP → OI → Greeks → Details → Trade → Basket ═══ */}
                    {(mobileView === 'puts' || mobileView === 'full') && <>
                      {/* Put LTP — closest to strike */}
                      <td className={`py-2 px-2 text-right ${peTint}`}>
                        <div className={`font-semibold font-mono-num text-[13px] ${isITM_PE ? 'text-rose-700' : 'text-gray-900'}`}>{safeLTP(strike.pe?.ltp, strike.pe)}</div>
                        {(() => { const txt = safeChg(strike.pe?.change_pct, strike.pe?.ltp); return txt ? (
                          <div className={`text-[10px] font-mono-num ${strike.pe?.change_pct > 0 ? 'text-green-600' : strike.pe?.change_pct < 0 ? 'text-red-500' : 'text-gray-400'}`}>{txt}</div>
                        ) : null; })()}
                      </td>

                      {/* Put OI with bar — adjacent to strike */}
                      <td className={`py-2 px-2 text-right oi-bar-cell ${peTint}`}>
                        <div className="oi-bar-fill put" style={{ width: `${peOIpct}%` }} />
                        <div className="relative z-10">
                          <div className="text-[12px] font-medium font-mono-num text-gray-800">{fmtOI(strike.pe)}</div>
                          <div className={`text-[10px] font-mono-num ${strike.pe?.oi_change_pct > 0 ? 'text-green-600' : strike.pe?.oi_change_pct < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                            {strike.pe?.oi_change_pct != null ? `${strike.pe.oi_change_pct >= 0 ? '+' : ''}${parseFloat(strike.pe.oi_change_pct).toFixed(1)}%` : ''}
                          </div>
                        </div>
                      </td>

                      {showGreeks && <td className={`py-2 px-1.5 text-[11px] text-right text-blue-600 font-mono-num hidden lg:table-cell ${peTint}`}>{peDelta}</td>}
                      {showGreeks && <td className={`py-2 px-1.5 text-[11px] text-right text-gray-500 font-mono-num hidden lg:table-cell ${peTint}`}>{peGamma}</td>}
                      {showGreeks && <td className={`py-2 px-1.5 text-[11px] text-right text-red-500 font-mono-num hidden lg:table-cell ${peTint}`}>{peTheta}</td>}
                      {showGreeks && <td className={`py-2 px-1.5 text-[11px] text-right text-gray-500 font-mono-num hidden xl:table-cell ${peTint}`}>{peVega}</td>}
                      {showGreeks && <td className={`py-2 px-1.5 text-[11px] text-right text-gray-400 font-mono-num hidden xl:table-cell ${peTint}`}>{peRho}</td>}
                      {showDetails && <td className={`py-2 px-1.5 text-[11px] text-right font-mono-num hidden sm:table-cell ${peTint}`}>{strike.pe?.oi_change != null ? fmtExact(strike.pe.oi_change) : '—'}</td>}
                      {showDetails && <td className={`py-2 px-1.5 text-[11px] text-right font-mono-num hidden sm:table-cell ${peTint}`}>{fmtExact(strike.pe?.volume)}</td>}
                      {showDetails && <td className={`py-2 px-1.5 text-[11px] text-right font-mono-num hidden sm:table-cell ${peTint}`}>{safeIV(strike.pe?.iv)}</td>}
                      {showDetails && <td className={`py-2 px-1.5 text-[11px] text-right hidden md:table-cell ${peTint}`}><span className={`font-mono-num ${safeChgColor(strike.pe?.change)}`}>{safeChgAbs(strike.pe?.change, strike.pe?.ltp)}</span></td>}
                      {showDetails && <td className={`py-2 px-1.5 text-[11px] text-right hidden md:table-cell ${peTint}`}><span className={`font-mono-num ${safeChgColor(strike.pe?.change_pct)}`}>{safeChg(strike.pe?.change_pct, strike.pe?.ltp)}</span></td>}

                      {/* PE Trade buttons */}
                      <td className={`py-1 px-1 ${peTint}`}>
                        <div className="flex gap-0.5 justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => basketMode ? toggleBasketCheck(basketKey_PE, "PE", "BUY") : handleTradeClick(strike, "PE", "BUY", strike.pe?.ltp, peGreeks)}
                            className="w-5 h-5 rounded bg-green-500 hover:bg-green-600 text-white text-[9px] font-bold flex items-center justify-center transition-colors" title="Buy" data-testid={`buy-pe-${strike.strike}`}>B</button>
                          <button onClick={() => basketMode ? toggleBasketCheck(basketKey_PE, "PE", "SELL") : handleTradeClick(strike, "PE", "SELL", strike.pe?.ltp, peGreeks)}
                            className="w-5 h-5 rounded bg-red-500 hover:bg-red-600 text-white text-[9px] font-bold flex items-center justify-center transition-colors" title="Sell" data-testid={`sell-pe-${strike.strike}`}>S</button>
                          <button onClick={(e) => { e.stopPropagation(); const ts = strike.pe?.tradingsymbol; if (ts) { navigate(`/options/${selectedSymbol}/${ts}?exchange=${currentInstrument?.derivExchange || 'NFO'}`); } }}
                            className="h-5 w-5 rounded bg-gray-100 hover:bg-blue-100 text-gray-500 hover:text-blue-600 flex items-center justify-center transition-colors" title={`Contract: ${strike.pe?.tradingsymbol || selectedSymbol + ' ' + strike.strike + ' PE'}`} data-testid={`contract-pe-${strike.strike}`}>
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      </td>

                      {/* PE Basket checkbox */}
                      {basketMode && <td className={`py-1 px-1 ${peTint}`}>
                        <div className="flex items-center justify-center" title={peIlliquid ? "Basket not allowed for illiquid contracts" : isInBasket_PE ? `${basketItem_PE.side} selected` : "Click B or S"}>
                          {isInBasket_PE ? (
                            <button onClick={() => toggleBasketCheck(basketKey_PE, "PE", basketItem_PE.side)}
                              className={`w-4 h-4 rounded text-[9px] font-bold flex items-center justify-center ${basketItem_PE.side === 'BUY' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}
                              data-testid={`basket-check-pe-${strike.strike}`}>{basketItem_PE.side === 'BUY' ? 'B' : 'S'}</button>
                          ) : (
                            <div className={`w-4 h-4 rounded border-2 ${peIlliquid ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-40' : 'border-gray-300'}`} data-testid={`basket-check-pe-${strike.strike}`} />
                          )}
                        </div>
                      </td>}
                    </>}
                  </tr>

                  {/* ═══ Spot Marker — seamless inline separator ═══ */}
                  {showSpotMarker && (() => {
                    const sp = liveSpotPrice || optionChain?.spot_price || 0;
                    const spClose = spotTicks[spotSymbol]?.close || optionChain?.spot_close || sp;
                    const absChg = sp - spClose;
                    const pctChg = spClose ? ((absChg / spClose) * 100) : 0;
                    return (
                    <tr data-testid="spot-price-marker-row">
                      <td colSpan={totalCols} className="p-0 relative" style={{ height: '24px' }}>
                        <div className="absolute inset-0 flex items-center w-full">
                          <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(0,200,150,0.4) 20%, rgba(0,200,150,0.4) 80%, transparent)' }} />
                          <div className="flex items-center gap-1 mx-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00C896] spot-pulse-dot" />
                            <span className="px-2 py-0.5 bg-[#00C896] text-white text-[9px] font-bold font-mono-num rounded-full whitespace-nowrap spot-badge-glow" data-testid="spot-price-badge">
                              ₹{sp?.toLocaleString('en-IN', { maximumFractionDigits: 2 })} | {pctChg >= 0 ? '+' : ''}{pctChg.toFixed(2)}%
                            </span>
                          </div>
                          <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(0,200,150,0.4) 20%, rgba(0,200,150,0.4) 80%, transparent)' }} />
                        </div>
                      </td>
                    </tr>
                    );
                  })()}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ Sticky Spot Price Pill (Groww-style) — always visible ═══ */}
      {optionChain?.spot_price && (
        <div className="sticky bottom-0 z-30 flex justify-center py-2 pointer-events-none" data-testid="sticky-spot-pill-container">
          <div className="pointer-events-auto inline-flex items-center gap-2 px-4 py-2 bg-white/95 backdrop-blur-md border border-slate-200 rounded-full shadow-lg" data-testid="sticky-spot-pill">
            <span className="w-2 h-2 rounded-full bg-[#00C896] animate-pulse" />
            <span className="text-xs font-medium text-slate-500">Spot</span>
            <span className={`text-sm font-bold font-mono-num ${spotFlash ? 'text-[#00C896]' : 'text-slate-900'} transition-colors duration-200`} data-testid="sticky-spot-price">
              ₹{(liveSpotPrice || optionChain?.spot_price)?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </span>
            {(() => {
              const sp = liveSpotPrice || optionChain?.spot_price || 0;
              const spClose = spotTicks[spotSymbol]?.close || optionChain?.spot_close || sp;
              const absChg = sp - spClose;
              const pctChg = spClose ? ((absChg / spClose) * 100) : 0;
              if (pctChg === 0) return null;
              return (
                <span className={`text-xs font-semibold font-mono-num ${pctChg >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="sticky-spot-change">
                  {pctChg >= 0 ? '+' : ''}{pctChg.toFixed(2)}%
                </span>
              );
            })()}
            {wsIsStreaming && (
              <span className="text-[9px] text-green-600 font-medium">LIVE</span>
            )}
          </div>
        </div>
      )}

      {/* ═══ Order Panel Dialog ═══ */}
      <Dialog open={orderPanelOpen} onOpenChange={setOrderPanelOpen}>
        <DialogContent className="bg-white rounded-2xl max-w-4xl max-h-[95vh] overflow-hidden p-0 font-manrope">
          <div className="flex flex-col md:flex-row h-auto md:h-[85vh]">
            {/* Left — Order Form */}
            <div className="w-full md:w-[380px] md:border-r border-b md:border-b-0 border-gray-200 flex flex-col">
              <div className="p-4 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">{selectedSymbol} {selectedStrike?.strike} {selectedOptionType}</h3>
                    <p className="text-sm text-gray-600 font-mono-num">
                      ₹{orderPrice || '—'}
                      {(() => {
                        const side = selectedOptionType === 'CE' ? selectedStrike?.ce : selectedStrike?.pe;
                        const chgPct = side?.change_pct;
                        if (chgPct == null || chgPct === 0) return null;
                        return <span className={`ml-1 ${chgPct > 0 ? 'text-green-600' : 'text-red-600'}`}>({chgPct >= 0 ? '+' : ''}{chgPct?.toFixed(2)}%)</span>;
                      })()}
                      {showDetails && (() => {
                        const side = selectedOptionType === 'CE' ? selectedStrike?.ce : selectedStrike?.pe;
                        const iv = side?.iv;
                        if (iv == null || iv === 0) return null;
                        return <span className="ml-2 text-xs text-purple-600 font-medium" data-testid="panel-iv-display">IV: {parseFloat(iv).toFixed(1)}%</span>;
                      })()}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-blue-600 text-xs">Depth</Button>
                </div>
              </div>
              <div className="p-4 border-b">
                <div className="flex gap-2">
                  <button onClick={() => setOrderType("delivery")} className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-colors ${orderType === "delivery" ? "bg-blue-100 text-blue-700 border-2 border-blue-500" : "bg-gray-100 text-gray-600"}`}>Delivery</button>
                  <button onClick={() => setOrderType("intraday")} className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-colors ${orderType === "intraday" ? "bg-blue-100 text-blue-700 border-2 border-blue-500" : "bg-gray-100 text-gray-600"}`}>Intraday</button>
                </div>
              </div>
              <div className="p-4 border-b">
                <div className="flex gap-2">
                  <button onClick={() => setOrderAction("BUY")} className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-colors ${orderAction === "BUY" ? "bg-green-500 text-white" : "bg-gray-100 text-gray-600"}`}>B</button>
                  <button onClick={() => setOrderAction("SELL")} className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-colors ${orderAction === "SELL" ? "bg-red-500 text-white" : "bg-gray-100 text-gray-600"}`}>S</button>
                </div>
              </div>
              <div className="p-4 border-b">
                <label className="text-sm text-gray-600 mb-2 block">Qty (Lot size: {currentInstrument.lotSize})</label>
                <div className="flex items-center gap-3">
                  <button onClick={() => setOrderQuantity(Math.max(1, orderQuantity - 1))} className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                  <Input type="number" value={orderQuantity} onChange={(e) => setOrderQuantity(Math.max(1, parseInt(e.target.value) || 1))} className="w-24 text-center font-bold text-lg h-10 font-mono-num" />
                  <button onClick={() => setOrderQuantity(orderQuantity + 1)} className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="p-4 border-b">
                <label className="text-sm text-gray-600 mb-2 block">Price Limit</label>
                <Input type="number" value={orderPrice} onChange={(e) => setOrderPrice(parseFloat(e.target.value) || 0)} className="h-12 font-bold text-lg font-mono-num" />
              </div>
              <div className="p-4 border-b">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Balance:</span>
                  <span className="font-semibold font-mono-num">₹{userBalance.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center text-sm mt-2">
                  <span className="text-gray-600">Approx req:</span>
                  <span className="font-semibold text-blue-600 font-mono-num">₹{(orderPrice * orderQuantity * currentInstrument.lotSize).toLocaleString()}</span>
                </div>
              </div>
              <div className="p-4 mt-auto">
                <Button onClick={handleExecuteTrade} data-testid="oc-execute-trade-btn"
                  disabled={!isMarketOpen()}
                  title={!isMarketOpen() ? "Trading is available only during market hours" : ""}
                  className={`w-full h-14 rounded-xl font-bold text-lg ${!isMarketOpen() ? "bg-gray-400 cursor-not-allowed" : orderAction === "BUY" ? "bg-green-500 hover:bg-green-600" : "bg-red-500 hover:bg-red-600"} text-white`}>
                  {!isMarketOpen() ? "Market Closed" : orderAction === "BUY" ? "Buy" : "Sell"}
                </Button>
              </div>
            </div>
            {/* Right — AI Advisory */}
            <div className="flex-1 overflow-y-auto bg-gray-50">
              <div className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Brain className="w-5 h-5 text-purple-600" />
                  <h3 className="font-bold text-base text-gray-900">AI Advisory</h3>
                  <Badge variant="outline" className="ml-auto text-purple-600 border-purple-300 text-xs">Deep Analysis</Badge>
                </div>
                {advisoryLoading ? (
                  <AdvisoryCard loading={true} />
                ) : advisoryData ? (
                  <AdvisoryCard advisory={advisoryData} onRetry={() => { if (selectedStrike) handleTradeClick(selectedStrike, selectedOptionType, orderAction, orderPrice, null); }} className="border-none shadow-none" />
                ) : (
                  <div className="text-center py-12 text-gray-400" data-testid="advisory-unavailable-placeholder">
                    <Bot className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p className="font-medium text-gray-500 text-sm">AI Advisory</p>
                    <p className="text-xs text-gray-400 mt-1">Click Buy/Sell on a strike to get AI analysis</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ Strategy Confirmation Dialog ═══ */}
      <Dialog open={confirmDialog} onOpenChange={(open) => { setConfirmDialog(open); if (!open) setStrategyLots(1); }}>
        <DialogContent className="bg-white rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto font-manrope">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-purple-600" />
              {aiStrategy?.strategy_name}
            </DialogTitle>
          </DialogHeader>
          {aiStrategy && (
            <div className="space-y-6 mt-4">
              {(aiStrategy.market_analysis || aiStrategy.why_this_strategy) && (
                <div className="p-5 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
                  <p className="text-sm font-bold text-amber-800 mb-3 flex items-center gap-2"><Brain className="w-4 h-4" /> AI Agent Analysis</p>
                  {aiStrategy.market_analysis && (<div className="mb-3"><p className="text-xs text-amber-700 font-semibold mb-1">Market Analysis:</p><p className="text-sm text-gray-700">{aiStrategy.market_analysis}</p></div>)}
                  {aiStrategy.why_this_strategy && (<div className="mb-3"><p className="text-xs text-amber-700 font-semibold mb-1">Why This Strategy:</p><p className="text-sm text-gray-700">{aiStrategy.why_this_strategy}</p></div>)}
                  {aiStrategy.risk_assessment && (<div><p className="text-xs text-amber-700 font-semibold mb-1">Risk Assessment:</p><p className="text-sm text-gray-700">{aiStrategy.risk_assessment}</p></div>)}
                </div>
              )}
              <div className="p-5 rounded-xl bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200">
                <div className="grid grid-cols-3 gap-4">
                  <div><p className="text-xs text-gray-600 mb-1">Strategy Type</p><p className="font-bold text-gray-900 capitalize">{aiStrategy.strategy_type}</p></div>
                  <div><p className="text-xs text-gray-600 mb-1">Underlying</p><p className="font-bold text-gray-900">{aiStrategy.underlying}</p></div>
                  <div><p className="text-xs text-gray-600 mb-1">Lot Size</p><p className="font-bold text-gray-900 font-mono-num">{currentInstrument.lotSize} qty/lot</p></div>
                </div>
                {(aiStrategy.profit_probability || aiStrategy.risk_reward_ratio) && (
                  <div className="mt-4 pt-4 border-t border-purple-200 grid grid-cols-2 gap-4">
                    {aiStrategy.profit_probability && (<div><p className="text-xs text-gray-600 mb-1">Profit Probability</p><p className="font-bold text-emerald-700">{aiStrategy.profit_probability}</p></div>)}
                    {aiStrategy.risk_reward_ratio && (<div><p className="text-xs text-gray-600 mb-1">Risk/Reward Ratio</p><p className="font-bold text-blue-700">{aiStrategy.risk_reward_ratio}</p></div>)}
                  </div>
                )}
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Quantity (Lots)</p>
                    <p className="text-xs text-gray-500">1 lot = {currentInstrument.lotSize} qty</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={() => setStrategyLots(Math.max(1, strategyLots - 1))} className="w-10 h-10 rounded-lg border-2 border-gray-300" disabled={strategyLots <= 1}><Minus className="w-4 h-4" /></Button>
                    <Input type="number" min="1" max="50" value={strategyLots} onChange={(e) => setStrategyLots(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))} className="w-20 h-10 text-center text-lg font-bold border-2 rounded-lg font-mono-num" />
                    <Button variant="outline" size="sm" onClick={() => setStrategyLots(Math.min(50, strategyLots + 1))} className="w-10 h-10 rounded-lg border-2 border-gray-300" disabled={strategyLots >= 50}><Plus className="w-4 h-4" /></Button>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  {[1, 2, 5, 10].map((lot) => (
                    <Button key={lot} variant={strategyLots === lot ? "default" : "outline"} size="sm" onClick={() => setStrategyLots(lot)}
                      className={`flex-1 ${strategyLots === lot ? 'bg-blue-600 text-white' : ''}`}>{lot} Lot{lot > 1 ? 's' : ''}</Button>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Strategy Legs</h3>
                <div className="space-y-3">
                  {aiStrategy.legs.map((leg, index) => (
                    <div key={index} className={`p-4 rounded-xl border-2 ${leg.action === 'BUY' ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${leg.action === 'BUY' ? 'bg-green-500' : 'bg-red-500'}`}><span className="text-white font-bold">{index + 1}</span></div>
                          <div><p className={`text-sm font-bold ${leg.action === 'BUY' ? 'text-emerald-700' : 'text-rose-700'}`}>{leg.action}</p><p className="font-bold text-gray-900">{leg.symbol} {leg.strike} {leg.option_type}</p></div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-600">Premium × {strategyLots} lot{strategyLots > 1 ? 's' : ''}</p>
                          <p className="text-lg font-bold text-gray-900 font-mono-num">₹{(leg.premium * strategyLots).toLocaleString()}</p>
                          <p className="text-xs text-gray-500 font-mono-num">({currentInstrument.lotSize * strategyLots} qty)</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-blue-50 border border-blue-200"><p className="text-xs text-blue-700 mb-1 font-semibold">Total Cost ({strategyLots} lot{strategyLots > 1 ? 's' : ''})</p><p className="text-2xl font-bold text-blue-900 font-mono-num">₹{(aiStrategy.total_cost * strategyLots).toLocaleString()}</p></div>
                <div className="p-4 rounded-xl bg-green-50 border border-green-200"><p className="text-xs text-emerald-700 mb-1 font-semibold">Max Profit</p><p className="text-2xl font-bold text-green-900 font-mono-num">{typeof aiStrategy.max_profit === 'number' ? `₹${(aiStrategy.max_profit * strategyLots).toLocaleString()}` : aiStrategy.max_profit}</p></div>
                <div className="p-4 rounded-xl bg-red-50 border border-red-200"><p className="text-xs text-rose-700 mb-1 font-semibold">Max Loss</p><p className="text-2xl font-bold text-red-900 font-mono-num">{typeof aiStrategy.max_loss === 'number' ? `₹${(aiStrategy.max_loss * strategyLots).toLocaleString()}` : aiStrategy.max_loss}</p></div>
              </div>
              <div className="flex gap-3 pt-4 border-t">
                <Button onClick={handleExecuteStrategy} className="flex-1 h-14 rounded-xl bg-gradient-to-r from-[#00C896] to-emerald-600 hover:from-[#00b386] hover:to-emerald-700 text-white font-bold text-lg"><Check className="w-6 h-6 mr-2" /> Execute {strategyLots} Lot{strategyLots > 1 ? 's' : ''}</Button>
                <Button onClick={() => { setConfirmDialog(false); setStrategyLots(1); }} variant="outline" className="flex-1 h-14 rounded-xl border-2"><X className="w-6 h-6 mr-2" /> Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══ Basket Floating Card ═══ */}
      {basketMode && basketItems.length > 0 && (
        <div className="fixed bottom-20 right-4 w-[420px] bg-white rounded-2xl border border-gray-200 shadow-2xl z-50 flex flex-col max-h-[55vh] font-manrope" data-testid="basket-floating-card">
          <div className="px-5 py-3 border-b bg-gray-50 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Target className="w-5 h-5 text-blue-600" />
              <h3 className="font-bold text-base text-gray-900">Basket Order</h3>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold" data-testid="basket-leg-count">{basketItems.length} leg{basketItems.length > 1 ? 's' : ''}</span>
            </div>
            <button onClick={() => { setBasketMode(false); setBasketItems([]); }} className="text-gray-400 hover:text-gray-600 p-1" data-testid="basket-panel-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="px-5 py-2.5 border-b flex items-center justify-between">
            <span className="text-sm text-gray-600 font-medium">Same qty for all legs</span>
            <button onClick={() => {
              const next = !sameQtyEnabled;
              setSameQtyEnabled(next);
              if (next) {
                const firstQty = basketItems[0]?.qty || currentInstrument.lotSize;
                setBasketItems(prev => prev.map(item => ({ ...item, qty: firstQty })));
              }
            }} className={`w-10 h-5.5 rounded-full relative transition-colors ${sameQtyEnabled ? 'bg-blue-600' : 'bg-gray-300'}`} data-testid="basket-same-qty-toggle">
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${sameQtyEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[220px]">
            {basketItems.map((item) => (
              <div key={item.key} className="px-5 py-3 border-b border-gray-100 flex items-center gap-3" data-testid={`basket-leg-${item.key}`}>
                <span className={`w-7 h-7 rounded-lg text-xs font-bold flex items-center justify-center flex-shrink-0 ${item.side === 'BUY' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>{item.side === 'BUY' ? 'B' : 'S'}</span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-bold text-gray-900">{item.strike} {item.option_type}</span>
                  {item.ltp != null && <span className="text-xs text-gray-500 ml-2 font-mono-num">₹{parseFloat(item.ltp || 0).toFixed(2)}</span>}
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => {
                    setBasketItems(prev => {
                      const updated = prev.map(x => x.key === item.key ? { ...x, qty: Math.max(currentInstrument.lotSize, x.qty - currentInstrument.lotSize) } : x);
                      if (sameQtyEnabled) { const newQty = updated.find(x => x.key === item.key)?.qty || currentInstrument.lotSize; return updated.map(x => ({ ...x, qty: newQty })); }
                      return updated;
                    });
                  }} className="w-6 h-6 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-bold">-</button>
                  <span className="text-sm font-bold w-10 text-center font-mono-num">{item.qty}</span>
                  <button onClick={() => {
                    setBasketItems(prev => {
                      const updated = prev.map(x => x.key === item.key ? { ...x, qty: x.qty + currentInstrument.lotSize } : x);
                      if (sameQtyEnabled) { const newQty = updated.find(x => x.key === item.key)?.qty || currentInstrument.lotSize; return updated.map(x => ({ ...x, qty: newQty })); }
                      return updated;
                    });
                  }} className="w-6 h-6 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-bold">+</button>
                </div>
                <button onClick={() => setBasketItems(prev => prev.filter(x => x.key !== item.key))} className="text-gray-300 hover:text-red-500 ml-1 p-0.5"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <div className="border-t bg-gray-50 rounded-b-2xl px-5 py-3 space-y-2.5">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 font-medium">Net Premium</span>
              <span className={`font-bold font-mono-num ${basketEstimatedPremium >= 0 ? 'text-red-600' : 'text-green-600'}`} data-testid="basket-premium">
                {basketEstimatedPremium >= 0 ? 'Debit' : 'Credit'} ₹{Math.abs(basketEstimatedPremium).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex justify-between text-xs text-gray-400">
              <span>Approx. Margin</span>
              <span className="font-semibold text-gray-600 font-mono-num">₹{Math.abs(basketEstimatedPremium * 1.2).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
            </div>
            <div className="flex gap-2.5 pt-1">
              <Button variant="outline" size="sm" onClick={() => setBasketItems([])} className="flex-1 h-10 text-sm border-gray-300 text-gray-600 font-medium" data-testid="basket-clear-btn">Clear All</Button>
              <Button onClick={buildStrategyFromBasket} disabled={buildingStrategy || basketItems.length === 0 || !isMarketOpen()}
                className="flex-1 h-10 text-sm bg-blue-600 hover:bg-blue-700 text-white font-bold" data-testid="basket-build-strategy-btn"
                title={!isMarketOpen() ? "Place Order is available only during market hours" : ""}>
                {buildingStrategy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                {!isMarketOpen() ? 'Market Closed' : 'Place Order'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Alert Creation Dialog ═══ */}
      <Dialog open={showAlertDialog} onOpenChange={setShowAlertDialog}>
        <DialogContent className="bg-white rounded-2xl max-w-md p-0 font-manrope">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="text-lg font-bold text-gray-900" data-testid="alert-dialog-title">
              Create Price Alert — {selectedSymbol}
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Alert Type</label>
              <div className="flex gap-2">
                {["above", "below", "crosses"].map(t => (
                  <button key={t} onClick={() => setAlertType(t)}
                    className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg border transition-colors capitalize ${alertType === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                    data-testid={`alert-type-${t}`}>{t}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Target Price</label>
              <Input type="number" value={alertPrice} onChange={(e) => setAlertPrice(e.target.value)}
                placeholder={`e.g., ${(liveSpotPrice || optionChain?.spot_price || 0).toFixed(0)}`}
                className="w-full font-mono-num" data-testid="alert-price-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Note (optional)</label>
              <Input value={alertNote} onChange={(e) => setAlertNote(e.target.value)} placeholder="e.g., resistance level breakout" className="w-full" data-testid="alert-note-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleCreateAlert} disabled={alertLoading || !alertPrice}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold" data-testid="alert-submit-btn">
                {alertLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Bell className="w-4 h-4 mr-1" />} Create Alert
              </Button>
              <Button onClick={() => setShowAlertDialog(false)} variant="outline" className="flex-1">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}


// ── Feature Flag Wrapper ──
export default function OptionChain() {
  if (!USE_PRO_UI) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin h-8 w-8 border-2 border-emerald-500 border-t-transparent rounded-full" /></div>}>
        <OptionChainClassic />
      </Suspense>
    );
  }
  return <OptionChainPro />;
}
